import { T, type Trilingual } from './i18n.ts';

/**
 * 通用设置页「操作习惯」那几项的纯函数（2026-10-29 接上）：
 *   ux_toastSec       提示条停留几秒（带撤销的多停 3 秒）
 *   ux_snoozeList     延后菜单里的选项（分钟数，或 eod = 到今天结束）
 *   ux_confirmDanger  删备忘、删事项这类操作：先做给撤销（undo，出厂），还是先弹窗确认（confirm）
 *   ux_gnav           G 然后一个字母跳页（要 ux_hk 总开关开着）
 * 纯的，不碰 DOM，有测试（ux-prefs.test.ts）。
 */

type Flat = Readonly<Record<string, unknown>>;

export const UNDO_EXTRA_MS = 3000;

/** 提示条停留多久（毫秒）。超出 1.5–20 秒的夹回去；读不出来用 3.2 秒（原来写死的那个数） */
export function toastMs(s: Flat, undo: boolean): number {
  const v = s['ux_toastSec'];
  const sec = typeof v === 'number' && Number.isFinite(v) ? Math.min(20, Math.max(1.5, v)) : 3.2;
  return Math.round(sec * 1000) + (undo ? UNDO_EXTRA_MS : 0);
}

export interface SnoozeChoice { minutes: number; eod: boolean }

/** 到今天（本机时间）结束还有几分钟，至少 1 分钟 */
export function minutesToEndOfDay(now: number): number {
  const d = new Date(now);
  const end = new Date(d.getFullYear(), d.getMonth(), d.getDate() + 1).getTime();
  return Math.max(1, Math.ceil((end - now) / 60_000));
}

/** 延后菜单的选项。一行一个；认不出的行跳过；去重；最多 8 个；一个都认不出就用出厂那一套 */
export function snoozeChoices(raw: unknown, now: number): SnoozeChoice[] {
  const parse = (text: string): SnoozeChoice[] => {
    const out: SnoozeChoice[] = [];
    for (const line of text.split(/[\n,，]/)) {
      const t = line.trim().toLowerCase();
      if (t === 'eod') {
        if (!out.some(x => x.eod)) out.push({ minutes: Math.min(1440, minutesToEndOfDay(now)), eod: true });
      } else if (/^\d{1,4}$/.test(t)) {
        const n = Number(t);
        if (n >= 1 && n <= 1440 && !out.some(x => !x.eod && x.minutes === n)) out.push({ minutes: n, eod: false });
      }
      if (out.length >= 8) break;
    }
    return out;
  };
  const got = typeof raw === 'string' ? parse(raw) : [];
  return got.length > 0 ? got : parse('5\n15\n60\neod');
}

export const dangerMode = (s: Flat): 'undo' | 'confirm' => (s['ux_confirmDanger'] === 'confirm' ? 'confirm' : 'undo');

/** G 然后哪个字母跳到哪一页（和原型 palette.js 的 GO 一致，加了 n = 事项提醒） */
export const GO_KEYS: Readonly<Record<string, string>> = {
  h: 'home', i: 'inbox', r: 'rules', w: 'wallet', p: 'tokenwatch', c: 'chain', n: 'reminders', m: 'memo', s: 'system', l: 'look', e: 'ext'
};

export const gnavOn = (s: Flat): boolean => s['ux_hk'] !== false && s['ux_gnav'] !== false;

/** 「快捷键」那张卡上的字 */
export const HK: Readonly<Record<string, Trilingual>> = {
  sub: T('只在这个网站里、光标不在输入框时有效。总开关和收件箱那几个键在上面「操作习惯」里改。',
    '只在這個網站裏、游標不在輸入框時有效。總開關和收件匣那幾個鍵在上面「操作習慣」裏改。',
    'Work inside this site when you are not typing in a field. Turn them off or change the inbox keys under Habits above.'),
  off: T('单键快捷键现在关着（上面「启用单键快捷键」）。', '單鍵快捷鍵現在關著（上面「啟用單鍵快捷鍵」）。', 'Single-key shortcuts are off (see Habits above).'),
  goTitle: T('跳页：先按 G，再按下面的字母', '跳頁：先按 G，再按下面的字母', 'Jump: press G, then a letter'),
  goOff: T('「G + 字母跳页」关着。', '「G + 字母跳頁」關著。', 'G-then-letter jumps are off.'),
  inboxTitle: T('收件箱里', '收件匣裏', 'In the inbox'),
  next: T('下一条', '下一條', 'Next'),
  prev: T('上一条', '上一條', 'Previous'),
  ack: T('确认', '確認', 'Acknowledge'),
  snooze: T('延后', '延後', 'Snooze'),
  done: T('处理完了', '處理完了', 'Done'),
  always: T('一直有效', '一直有效', 'Always'),
  esc: T('关掉弹出来的框', '關掉彈出來的框', 'Close a dialog')
};

/** 把一个延后选项说成人话 */
export const snoozeLabel = (c: SnoozeChoice, lang: 'zh-CN' | 'zh-HK' | 'en'): string => {
  if (c.eod) return lang === 'en' ? 'Until end of day' : lang === 'zh-HK' ? '到今天結束' : '到今天结束';
  const m = c.minutes;
  if (m % 60 === 0) {
    const h = m / 60;
    return lang === 'en' ? `${h} h` : `${h} 小时`.replace('小时', lang === 'zh-HK' ? '小時' : '小时');
  }
  return lang === 'en' ? `${m} min` : `${m} 分钟`.replace('分钟', lang === 'zh-HK' ? '分鐘' : '分钟');
};
